import * as THREE from 'three'

const IOS_TEXTURE_MAX_SIZE = 2048
const TEXTURE_PROPERTY_NAMES = [
  'map',
  'normalMap',
  'emissiveMap',
  'aoMap',
  'alphaMap',
  'roughnessMap',
  'metalnessMap',
  'displacementMap',
] as const

type TexturePropertyName = (typeof TEXTURE_PROPERTY_NAMES)[number]
type TextureBearingMaterial = THREE.Material & Partial<Record<TexturePropertyName, THREE.Texture | null>>

/** iOS Safari は大きい texture を GPU に載せる時にタブごと落ちやすいため、軽量化対象を判定する。 */
const shouldUseIosSafeMaterial = (): boolean => {
  if (typeof navigator === 'undefined') return false

  const userAgent = navigator.userAgent
  const isIosDevice = /iP(hone|ad)/.test(userAgent)
  const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1

  return isIosDevice || isIpadOs
}

const isTextureBearingMaterial = (material: THREE.Material): material is TextureBearingMaterial => {
  return TEXTURE_PROPERTY_NAMES.some((propertyName) => propertyName in material)
}

/** texture.image が Canvas で縮小できる画像サイズを持つかを安全に取り出す。 */
const getImageSize = (image: unknown): { width: number; height: number } | null => {
  if (!image || typeof image !== 'object') return null
  if (!('width' in image) || !('height' in image)) return null

  const { width, height } = image
  if (typeof width !== 'number' || typeof height !== 'number') return null
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null
  if (width <= 0 || height <= 0) return null

  return { width, height }
}

/** drawImage に渡せる image 実体だけを縮小対象にする。 */
const canDrawTextureImage = (image: unknown): image is CanvasImageSource => {
  if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement) return true
  if (typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement) return true
  if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) return true
  if (typeof OffscreenCanvas !== 'undefined' && image instanceof OffscreenCanvas) return true

  return false
}

/** 見た目の色は残しつつ、4096px などの巨大 texture を iOS 向け上限まで縮小する。 */
const resizeTextureImage = (texture: THREE.Texture, maxSize: number): void => {
  const size = getImageSize(texture.image)
  if (!size) return
  if (Math.max(size.width, size.height) <= maxSize) return
  if (!canDrawTextureImage(texture.image)) return

  const scale = maxSize / Math.max(size.width, size.height)
  const width = Math.max(1, Math.round(size.width * scale))
  const height = Math.max(1, Math.round(size.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d', { alpha: true })
  if (!context) return

  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(texture.image, 0, 0, width, height)

  texture.image = canvas
  texture.needsUpdate = true
}

/** material が参照している texture map を重複処理しないように縮小する。 */
const resizeMaterialTextures = (material: THREE.Material, resizedTextures: Set<THREE.Texture>): void => {
  if (!isTextureBearingMaterial(material)) return

  for (const propertyName of TEXTURE_PROPERTY_NAMES) {
    const texture = material[propertyName]
    if (!texture || resizedTextures.has(texture)) continue

    resizeTextureImage(texture, IOS_TEXTURE_MAX_SIZE)
    resizedTextures.add(texture)
  }
}

/** VRM scene 内の material を巡回し、iOS だけ texture を GPU 登録前に軽量化する。 */
export const applyIosTextureResize = (scene: THREE.Object3D): void => {
  if (!shouldUseIosSafeMaterial()) return

  const resizedTextures = new Set<THREE.Texture>()
  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return

    const materials = Array.isArray(object.material) ? object.material : [object.material]
    for (const material of materials) {
      resizeMaterialTextures(material, resizedTextures)
    }
  })
}
